import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../Table/Table";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import PopUp from "../PopUp";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const Simulazioni = () => {
  const [loading, setLoading] = useState(false);
  const [simulazioni, setSimulazioni] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedSimulazioneId, setSelectedSimulazioneId] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const downloadRef = useRef(null);

  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const isoDatePart = dateString.slice(0, 10);
    const [year, month, day] = isoDatePart.split("-");
    if (!year || !month || !day) return "";

    return `${day}.${month}.${year}`;
  };
  const fetchSimulazioni = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        const filterParams = buildFilterQueryParams(
          filtersParam,
          filterFnsParam
        );

        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
        };

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        const response = await api.get("/simulazioni", {
          params,
        });

        const { content = [], page: pageInfo } = response.data ?? {};

        const formatted = content.map((sim) => ({
          ...sim,
          dataInserimento: formatDate(sim.dataInserimento),
        }));

        setSimulazioni(formatted);
        setTotalRows(pageInfo?.totalElements ?? content.length);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns]
  );

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      alert("Accedi come amministratore per visualizzare le simulazioni!");
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;
    
    fetchSimulazioni({
      pageParam: page,
      sizeParam: pageSize,
    });
  }, [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns, fetchSimulazioni]);

  const handlePageChange = (pageNumber) => {
    setPage(Math.max(pageNumber - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleRowClick = (simulazione) => {
    navigate(`/simulazione/${simulazione.idSimulazioneTrainer}`);
  };

  const handleDelete = async () => {
    if (!selectedSimulazioneId) {
      return;
    }
    try {
      const response = await api.delete(
        `/simulazioni/${selectedSimulazioneId}`
      );
      if (response.status === 204) {
        toast.success("Simulazione eliminata con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        fetchSimulazioni({
          pageParam: page,
          sizeParam: pageSize,
        });
      }
    } catch (error) {
      console.error(error);

      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setSelectedSimulazioneId(null);
      setShowPopUp(false);
    }
  };

  const columns = [
    { field: "dataInserimento", header: "Data Inserimento" },
    {
      field: "link",
      header: "Link",
      Cell: ({ row }) => {
        const url = row.original.link;
        return url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#BD1E2C", textDecoration: "underline" }}
            onClick={(e) => e.stopPropagation()}
          >
            {url}
          </a>
        ) : null;
      },
    },
    { field: "autore", header: "Autore" },
    { field: "trainerCodiceFiscale", header: "Codice Fiscale" },
    {
      header: "Azioni",
      accessorKey: "azioni",
      Cell: ({ row }) => (
        <button
          className="outside-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedSimulazioneId(row.original.idSimulazioneTrainer);
            setShowPopUp(true);
          }}
        >
          <i className="fa-solid fa-trash"></i> Elimina
        </button>
      ),
    },
  ];

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/simulazioni", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `simulazioni.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <PopUp
        open={showPopUp}
        setOpen={setShowPopUp}
        header="Conferma Eliminazione"
        content="Sei sicuro di voler rimuovere questa simulazione?"
        deleteFunction={handleDelete}
      />

      <div className="page-header">
        <div className="page-title">Simulazioni</div>
        <div className="controls">
          <div className="download-menu" ref={downloadRef}>
            <button
              className="download-btn"
              onClick={() => setIsDownloadOpen((prev) => !prev)}
            >
              Scarica
            </button>
            {isDownloadOpen && (
              <div className="download-dropdown">
                <button onClick={() => handleExport("csv")}>CSV</button>
                <button onClick={() => handleExport("xlsx")}>Excel</button>
              </div>
            )}
          </div>
          <div className="add-btn">
            <button onClick={() => navigate("/aggiungi-simulazione")}>
              + Nuova Simulazione
            </button>
          </div>
        </div>
      </div>

      <div className="page-content simulazioni">
        <Table
          columns={columns}
          data={simulazioni}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default Simulazioni;
